import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Auth } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';

import { NvButton } from '../../ui/button/nv-button';
import { NvDialogActions, NvDialogContent, NvDialogTitle } from '../../ui/dialog/nv-dialog-parts';
import { NV_DIALOG_DATA, NvDialogRef, NvDialogService } from '../../ui/dialog/nv-dialog.service';
import { NvIcon } from '../../ui/icon/nv-icon';
import { NvSpinner } from '../../ui/misc/nv-spinner';
import { NvStatus, NvTone } from '../../ui/status/nv-status';
import { NvToastService } from '../../ui/toast/nv-toast';
import { FloorStore } from '../../../core/stores/floor.store';
import { OrdersStore } from '../../../core/stores/orders.store';
import { PendingRequestsService } from '../../../core/notifications/pending-requests.service';
import { RewardPinResetDialog, RewardPinResetDialogData } from '../reward-pin-reset-dialog/reward-pin-reset-dialog';
import { GuestRequest } from '../../models/guest-request.model';
import { PermissionService } from '../../../core/permissions/permission.service';
import { TenancyService } from '../../../core/tenancy/tenancy.service';
import { TableService } from '../../services/table.service';
import { OrdersService } from '../../services/orders.service';
import { Order, OrderLine } from '../../models/order.model';
import { KotTicket } from '../../models/kot.model';
import { formatMinorAsRupeesClient } from '../../utils/money.util';
import { confirm } from '../../utils/confirm.util';
import { promptRejectOrderReason } from '../../utils/reject-order.util';
import { toMillis } from '../../utils/timestamp.util';
import { REQUEST_REASON_LABEL } from '../../models/guest-request-labels';
import { ApproveOrderDialog, ApproveOrderDialogData, ApproveOrderDialogResult } from '../approve-order-dialog/approve-order-dialog';
import {
  buildTableLineBuckets,
  computeServedLineIds,
  filterLiveTickets,
  kotLabel,
  lineStatusChip,
  UPCOMING_TICKET_STATUSES,
} from '../../utils/table-line-buckets.util';

export interface TableDetailDialogData {
  tableId: string;
  /**
   * Round 4, §4 — Orders' own rows and board cards were unclickable (the
   * mock isn't any richer here either: its cards just navigate to Billing,
   * `mock:3125,3129`, rather than opening a detail surface). Reusing this
   * dialog rather than building a second one — set when opened from Orders
   * to confine the view to that single order: `tableOrders` below filters
   * to it, the title becomes that order's own, and the table-wide actions
   * (Clear table, Pending requests) that make no sense for one order are
   * hidden. Undefined preserves the original whole-table view.
   */
  orderId?: string;
}

type LineState = 'waiting_approval' | 'in_kitchen' | 'ready' | 'served' | 'unavailable';

interface OrderLineView {
  line: OrderLine;
  state: LineState;
}

interface OrderView {
  order: Order;
  lines: OrderLineView[];
  readyLineIds: string[];
}

interface StatusView {
  tone: NvTone;
  label: string;
}

const LINE_STATE_LABEL: Record<LineState, string> = {
  waiting_approval: 'Waiting approval',
  in_kitchen: 'In kitchen',
  ready: 'Ready to serve',
  served: 'Served',
  unavailable: 'Unavailable',
};

/**
 * Concern 10 — clicking an occupied table on the floor used to do nothing at
 * all (floor-view.html's card had no click binding; only its own inner
 * buttons acted). This is the single live table-detail surface it now
 * opens: every order placed so far with each line's real kitchen state, the
 * running visit total, and any pending guest requests — all live signals
 * (FloorStore/OrdersStore/PendingRequestsService), not a one-time snapshot,
 * so an action taken from inside this dialog (approve, mark served,
 * acknowledge) is reflected the moment the write lands rather than only on
 * next open. Also opened by TableKotBoard (Orders → Tables), which used to
 * have its own separate, read-only detail dialog — retired in favour of
 * this one shared surface.
 *
 * Lives in shared/components rather than a feature folder because it's
 * opened from two different features (tables/floor-view and
 * orders/table-kot-board), and this app's lint boundary forbids one feature
 * importing from another — shared is the correct home for anything more
 * than one feature needs to open.
 *
 * Reuses table-line-buckets.util's bucket-building helpers (also shared,
 * for the same cross-feature reason) rather than re-deriving "waiting
 * approval / in kitchen / ready" a second way — this dialog just narrows
 * them to one table and folds in the "served" and "unavailable" states
 * those buckets don't need to track themselves. The status pill's tone/
 * label mapping is its own small local copy of floor-view's table-view
 * model rather than an import from it, for the same feature-boundary
 * reason — floor-view's own richer band/urgent logic isn't needed here.
 */
@Component({
  selector: 'nv-table-detail-dialog',
  imports: [FormsModule, NvButton, NvDialogActions, NvDialogContent, NvDialogTitle, NvIcon, NvSpinner, NvStatus],
  templateUrl: './table-detail-dialog.html',
  styleUrl: './table-detail-dialog.scss',
})
export class TableDetailDialog {
  private readonly dialogRef = inject<NvDialogRef<void>>(NvDialogRef);
  private readonly dialog = inject(NvDialogService);
  private readonly floor = inject(FloorStore);
  private readonly ordersStore = inject(OrdersStore);
  private readonly orders = inject(OrdersService);
  private readonly tableService = inject(TableService);
  private readonly pendingRequestsService = inject(PendingRequestsService);
  private readonly permissions = inject(PermissionService);
  private readonly tenancy = inject(TenancyService);
  private readonly toasts = inject(NvToastService);
  private readonly auth = inject(Auth);
  readonly data = inject<TableDetailDialogData>(NV_DIALOG_DATA);

  readonly formatPrice = formatMinorAsRupeesClient;
  readonly lineStateLabel = LINE_STATE_LABEL;
  /** Per-line "Ready · Ravi" / "Served · Anil" attribution (chef/waiter PIN flow) and the line's KOT number. */
  readonly lineChip = lineStatusChip;
  readonly kotLabel = kotLabel;
  readonly reasonLabel = REQUEST_REASON_LABEL;

  readonly canManage = computed(() => this.permissions.can('manage_orders'));
  /** Rewards-PIN reset — same permission the rewards settings page requires. */
  readonly canSetPin = computed(() => this.permissions.can('manage_rewards'));
  /** Separate from canManage — a ticket's own new/accepted/preparing/ready walk is gated on manage_kot server-side (firestore.rules' kotTickets rule), the same permission Orders → Kitchen tickets uses, not manage_orders. */
  readonly canManageKot = computed(() => this.permissions.can('manage_kot'));
  /** Same gate as floor-view.ts's own canSeePhone — the two surfaces must never disagree about who may see a guest's number. */
  readonly canSeePhone = computed(() => this.tenancy.isAdmin());

  // ------------------------------------------------------- Round 4, §3e
  /**
   * Editable here (not just displayed) because a party's size genuinely
   * changes mid-visit — someone joins, someone leaves before the bill. Any
   * staff member who can act on the table at all may set it; it's not
   * gated behind canManage() the way order actions are, since it's floor
   * bookkeeping, not an order decision.
   */
  readonly partySizeInput = signal<number | null>(null);
  readonly savingPartySize = signal(false);
  private partySizeTouched = false;

  readonly partySizeChanged = computed(() => {
    const current = this.status()?.partySize ?? null;
    return this.partySizeInput() !== current;
  });

  constructor() {
    // status() loads asynchronously and can also change under the dialog
    // (a table clearing, a new session starting) — only re-seeds the input
    // while the manager hasn't started typing their own edit, same
    // touched-guard shape as venue-settings.ts's zonesInput.
    effect(() => {
      const loaded = this.status()?.partySize ?? null;
      if (!this.partySizeTouched) this.partySizeInput.set(loaded);
    });
  }

  /** Angular's NumberValueAccessor (input[type=number] + ngModel) already emits a number or null — no string parsing needed here. */
  onPartySizeInput(value: number | null): void {
    this.partySizeTouched = true;
    this.partySizeInput.set(value !== null && value > 0 ? Math.floor(value) : null);
  }

  async savePartySize(): Promise<void> {
    const value = this.partySizeInput();
    if (value === null) return;
    this.savingPartySize.set(true);
    try {
      await this.tableService.updateSessionPartySize(this.data.tableId, value);
      this.partySizeTouched = false;
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not save the party size.', 'Dismiss', {
        duration: 4000,
      });
    } finally {
      this.savingPartySize.set(false);
    }
  }

  readonly table = computed(() => this.floor.tables().find((t) => t.id === this.data.tableId));
  readonly status = computed(() => this.floor.statuses().find((s) => s.tableId === this.data.tableId));

  readonly pendingRequests = computed(() => this.pendingRequestsService.pendingByTable().get(this.data.tableId) ?? []);

  /** Same tone/label mapping as table-view.model.ts's toFloorCardView — not imported from it (see class doc comment), just the small subset this header needs. */
  readonly statusView = computed<StatusView | null>(() => {
    const status = this.status();
    if (!status) return null;
    const requests = this.pendingRequests();
    if (requests.length > 0) {
      return {
        tone: 'danger',
        label: requests.length === 1 ? REQUEST_REASON_LABEL[requests[0].reason] : 'Attention',
      };
    }
    if (status.occupied) return { tone: 'neutral', label: 'Occupied' };
    if (status.cleaningStatus === 'dirty') return { tone: 'neutral', label: 'Needs cleaning' };
    if (status.cleaningStatus === 'cleaning') return { tone: 'neutral', label: 'Being cleaned' };
    return { tone: 'success', label: 'Available' };
  });

  /** Round 4, §4 — true when this dialog was opened to show one specific order (Orders → row/card click) rather than the whole table's visit. Drives the title, hides the table-wide Clear table action and the Pending requests section, neither of which means anything scoped to a single order. */
  readonly singleOrderMode = computed(() => this.data.orderId !== undefined);

  /**
   * Every order still part of the current, unsettled visit — a settled/
   * cancelled order is excluded the same way visitTotalMinor below is, so a
   * bill already paid off doesn't linger here as if it were still open.
   * Narrowed to `data.orderId` alone in single-order mode (which shows
   * whatever specific order it was asked for, regardless of the checks
   * below — a manager who clicked through to one order still needs to see
   * it).
   *
   * Round 8, §10 — a `served` order marked "Free / complaint…" never gets a
   * bill (markOrderComp deliberately never advances `status` — see
   * Order.compType's own doc comment) and so never reaches `'settled'`
   * either: without this exclusion it stayed in this "current" list
   * forever, indistinguishable from an order genuinely still awaiting
   * billing. `pending_approval`/`open` orders are NOT excluded when comped
   * — comping doesn't skip kitchen prep or approval, it only means this
   * order will never be billed once served.
   */
  readonly tableOrders = computed<Order[]>(() =>
    this.ordersStore
      .orders()
      .filter(
        (o) =>
          o.tableId === this.data.tableId &&
          (this.data.orderId !== undefined
            ? o.id === this.data.orderId
            : o.status === 'pending_approval' || o.status === 'open' || (o.status === 'served' && !o.compType))
      )
      .sort((a, b) => (toMillis(a.createdAt) ?? 0) - (toMillis(b.createdAt) ?? 0))
  );

  readonly visitTotalMinor = computed(() => this.tableOrders().reduce((sum, o) => sum + o.totalMinor, 0));

  private readonly liveTickets = computed(() => filterLiveTickets(this.ordersStore.orders(), this.ordersStore.tickets()));
  private readonly servedLineIds = computed(() => computeServedLineIds(this.ordersStore.orders()));

  private readonly buckets = computed(() =>
    buildTableLineBuckets(this.data.tableId, this.ordersStore.orders(), this.liveTickets(), this.servedLineIds())
  );

  private lineKey(orderId: string, lineId: string): string {
    return `${orderId}:${lineId}`;
  }

  /**
   * bugfix/core-workflow-issues — line key -> the live KOT ticket still
   * carrying it, for any ticket not yet 'ready'. Lets the table dialog
   * offer a one-tap "Mark ready" shortcut (fastTrackTicketReady below)
   * instead of sending staff to the separate Orders -> Kitchen tickets
   * board for every single item — real friction for a venue with no
   * separate kitchen station, where whoever is on the floor is also the
   * one cooking.
   */
  private readonly upcomingTicketByLineKey = computed(() => {
    const map = new Map<string, KotTicket>();
    for (const ticket of this.liveTickets()) {
      if (ticket.tableId !== this.data.tableId) continue;
      if (!UPCOMING_TICKET_STATUSES.includes(ticket.status)) continue;
      for (const line of ticket.lines) {
        map.set(this.lineKey(ticket.orderId, line.lineId), ticket);
      }
    }
    return map;
  });

  /** null once the ticket has reached 'ready' (or was never found) — the template falls back to the plain "In kitchen" label in that case, same as before this shortcut existed. */
  ticketForLine(orderId: string, lineId: string): KotTicket | null {
    return this.upcomingTicketByLineKey().get(this.lineKey(orderId, lineId)) ?? null;
  }

  /** Every order, each active line resolved to its real display state — waiting approval, in kitchen, ready, served, or (for a partially-approved order) unavailable. */
  readonly orderViews = computed<OrderView[]>(() => {
    const served = this.servedLineIds();
    const readyKeys = new Set(this.buckets().ready.map((i) => this.lineKey(i.orderId, i.lineId)));
    const upcomingKeys = new Set(this.buckets().upcoming.map((i) => this.lineKey(i.orderId, i.lineId)));

    return this.tableOrders().map((order): OrderView => {
      const lines: OrderLineView[] = order.lines
        .filter((l) => l.status !== 'void')
        .map((line) => {
          const key = this.lineKey(order.id, line.lineId);
          let state: LineState;
          if (line.status === 'rejected') state = 'unavailable';
          else if (order.status === 'served' || served.has(key)) state = 'served';
          else if (readyKeys.has(key)) state = 'ready';
          else if (upcomingKeys.has(key)) state = 'in_kitchen';
          else state = 'waiting_approval';
          return { line, state };
        });
      const readyLineIds = lines.filter((l) => l.state === 'ready').map((l) => l.line.lineId);
      return { order, lines, readyLineIds };
    });
  });

  private readonly servingLineKeys = signal<ReadonlySet<string>>(new Set());
  private readonly servingOrderIds = signal<ReadonlySet<string>>(new Set());

  isServingLine(orderId: string, lineId: string): boolean {
    return this.servingLineKeys().has(this.lineKey(orderId, lineId));
  }

  isServingOrder(orderId: string): boolean {
    return this.servingOrderIds().has(orderId);
  }

  private acknowledging = signal<ReadonlySet<string>>(new Set());

  isAcknowledging(requestId: string): boolean {
    return this.acknowledging().has(requestId);
  }

  /** Opens the Set PIN dialog for a `pin_reset` request; the dialog acknowledges + resolves the request itself once the PIN is set. */
  openSetPin(request: GuestRequest): void {
    const number = this.status()?.number;
    this.dialog.open<boolean, RewardPinResetDialogData>(RewardPinResetDialog, {
      width: '360px',
      data: {
        requestId: request.id,
        tableLabel: number !== undefined ? `Table ${number}` : 'This table',
        guestName: request.guestName || null,
        guestPhone: request.guestPhone,
      },
    });
  }

  async acknowledgeRequest(requestId: string): Promise<void> {
    this.acknowledging.update((ids) => new Set(ids).add(requestId));
    try {
      await this.pendingRequestsService.acknowledge(requestId);
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not acknowledge that.', 'Dismiss', { duration: 4000 });
    } finally {
      this.acknowledging.update((ids) => {
        const next = new Set(ids);
        next.delete(requestId);
        return next;
      });
    }
  }

  async markLineServed(orderId: string, lineId: string): Promise<void> {
    const key = this.lineKey(orderId, lineId);
    if (this.servingLineKeys().has(key)) return;
    this.servingLineKeys.update((keys) => new Set(keys).add(key));
    try {
      const result = await this.orders.markOrderLineServed(orderId, lineId);
      // Offline — the line does NOT show as served (orderViews only reacts
      // to the real order/ticket state), just tells staff the tap was saved
      // rather than lost.
      if ('queued' in result) {
        this.toasts.open("You're offline — saved, it'll send when you're back.", 'Dismiss', { duration: 5000 });
      }
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not mark that served.', 'Dismiss', { duration: 4000 });
    } finally {
      this.servingLineKeys.update((keys) => {
        const next = new Set(keys);
        next.delete(key);
        return next;
      });
    }
  }

  private readonly fastTrackingTicketIds = signal<ReadonlySet<string>>(new Set());

  isFastTrackingTicket(ticketId: string): boolean {
    return this.fastTrackingTicketIds().has(ticketId);
  }

  /**
   * One-tap "ready" shortcut from the table card. Walks the ticket through
   * whatever steps firestore.rules' compare-and-swap chain still requires
   * (new -> accepted -> preparing -> ready) rather than relaxing that rule
   * to allow a direct jump — the rule's own comment is explicit that a
   * ticket skipping straight from 'new' to 'ready' is deliberately refused,
   * and the middle steps are what populate acceptedAt/preparingAt (kitchen
   * timing data other surfaces read). This only collapses the UI into one
   * tap; the underlying ticket still passes through every real state, in
   * order, exactly as if a kitchen station had tapped through them one at
   * a time on the Kitchen tickets board.
   */
  async fastTrackTicketReady(ticket: KotTicket): Promise<void> {
    if (this.fastTrackingTicketIds().has(ticket.id)) return;
    this.fastTrackingTicketIds.update((ids) => new Set(ids).add(ticket.id));
    try {
      if (ticket.status === 'new') {
        const uid = this.auth.currentUser?.uid;
        if (!uid) throw new Error('Sign in again to do this.');
        await this.orders.acceptTicket(ticket.id, uid);
      }
      if (ticket.status === 'new' || ticket.status === 'accepted') {
        await this.orders.startPreparing(ticket.id);
      }
      await this.orders.markTicketReady(ticket.id);
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not mark that ready.', 'Dismiss', { duration: 4000 });
    } finally {
      this.fastTrackingTicketIds.update((ids) => {
        const next = new Set(ids);
        next.delete(ticket.id);
        return next;
      });
    }
  }

  async markAllServed(orderId: string): Promise<void> {
    if (this.servingOrderIds().has(orderId)) return;
    this.servingOrderIds.update((ids) => new Set(ids).add(orderId));
    try {
      const result = await this.orders.markOrderServed(orderId);
      if ('queued' in result) {
        this.toasts.open("You're offline — saved, it'll send when you're back.", 'Dismiss', { duration: 5000 });
      }
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not mark this order served.', 'Dismiss', { duration: 4000 });
    } finally {
      this.servingOrderIds.update((ids) => {
        const next = new Set(ids);
        next.delete(orderId);
        return next;
      });
    }
  }

  private tableLabel(): string {
    const number = this.table()?.number;
    return number !== undefined ? `table ${number}` : 'this table';
  }

  private async dispatchAssistance(order: Order, note: string): Promise<void> {
    await this.pendingRequestsService.dispatchAssistance(order.tableId, note, `Table ${this.table()?.number ?? order.tableId}`).catch(() => undefined);
  }

  /** Same item-picker flow as the other three approval surfaces (order-approval-strip/active-orders-list/orders-board) — always shows the guest's order before committing to anything. */
  async showItems(order: Order): Promise<void> {
    const result = await firstValueFrom(
      this.dialog
        .open<ApproveOrderDialogResult | undefined, ApproveOrderDialogData>(ApproveOrderDialog, {
          data: { order },
        })
        .afterClosed()
    );
    if (!result) return;

    if (result.assistManually) {
      try {
        await this.orders.rejectOrder(
          order.id,
          "None of these items are available right now — a team member is on their way to help you choose something else."
        );
        await this.dispatchAssistance(order, 'Order unavailable — guest needs help choosing alternatives.');
        this.toasts.open('Order rejected — a team member has been notified.', 'Dismiss', { duration: 3000 });
      } catch (err) {
        this.toasts.open(err instanceof Error ? err.message : 'Could not reject the order.', 'Dismiss', { duration: 4000 });
      }
      return;
    }

    const totalLines = order.lines.filter((l) => l.status === 'active').length;
    const message = result.approvedLineIds
      ? `Approved ${result.approvedLineIds.length} of ${totalLines} items — ${this.tableLabel()} needs attention.`
      : `Order approved — ${this.tableLabel()}.`;
    try {
      await this.orders.approveOrder(order.id, result.approvedLineIds);
      if (result.approvedLineIds) {
        await this.dispatchAssistance(order, 'Some items on this order are unavailable — guest may want alternatives.');
      }
      this.toasts.open(message, 'Dismiss', { duration: 3000 });
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not approve the order.', 'Dismiss', { duration: 4000 });
    }
  }

  async reject(order: Order): Promise<void> {
    const reason = await promptRejectOrderReason(this.dialog, this.tableLabel());
    if (reason === null) return;
    try {
      await this.orders.rejectOrder(order.id, reason || undefined);
      this.toasts.open(`Order rejected — ${this.tableLabel()}.`, 'Dismiss', { duration: 3000 });
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not reject the order.', 'Dismiss', { duration: 4000 });
    }
  }

  readonly clearing = signal(false);

  /**
   * Item 16 — the exact client-side mirror of floor-view.ts's own
   * `unbilledServedOrdersByTable`, narrowed to this one table: served
   * orders with no bill drafted yet (billId null) and not comped. Read
   * straight off OrdersStore rather than this dialog's own `tableOrders`
   * so the two surfaces can never disagree about what "still owed" means
   * (tableOrders is filtered for display and would miss a served order
   * that already left the current-visit window).
   */
  private readonly unbilledServedOrders = computed(() =>
    this.ordersStore
      .orders()
      .filter((o) => o.tableId === this.data.tableId && o.status === 'served' && !o.billId && !o.compType)
  );

  /**
   * Item 16 — same condition as floor-view.ts's `hasUnsettledBill`: a bill
   * already drafted for this table, or served orders never billed at all.
   * A water-bottle (or any other) request that became an order is counted
   * by exactly this check like any other order — deliberately not
   * special-cased. Clear table is DISABLED while this is true, rather than
   * letting staff tap it and get the generic server-side refusal toast.
   */
  readonly hasUnsettledBill = computed(
    () => this.floor.draftBillByTable().has(this.data.tableId) || this.unbilledServedOrders().length > 0
  );

  /** Short hint shown under the disabled Clear table button so the reason is visible, not just the disabled state. */
  readonly clearBlockedReason = computed(() => (this.hasUnsettledBill() ? 'Settle the bill first' : null));

  /** Same clearTable callable and confirm copy as floor-view.ts's own clear() — cancels any dangling pending_approval/open order for this table before ending the session. Closes this dialog on success since the table is no longer occupied. */
  async clearTable(): Promise<void> {
    const table = this.table();
    if (!table) return;
    // Item 16 — the button is disabled in this state, so this only catches
    // a bill that became unsettled between render and tap. Returning here
    // (rather than opening the confirm and letting the server refuse)
    // keeps staff from ever seeing the generic failure toast for this case.
    if (this.hasUnsettledBill()) return;
    const confirmed = await firstValueFrom(
      confirm(this.dialog, {
        title: `Clear table ${table.number}?`,
        message: 'Use this once the bill is settled. The next guest at this table will be asked for their number.',
        confirmLabel: 'Clear table',
      })
    );
    if (!confirmed) return;
    this.clearing.set(true);
    try {
      const result = await this.tableService.clearTable(table.id);
      const suffix =
        result.cancelledOrders > 0
          ? ` ${result.cancelledOrders} unfinished order${result.cancelledOrders === 1 ? ' was' : 's were'} cancelled.`
          : '';
      this.toasts.open(`Table ${table.number} cleared.${suffix}`, 'Dismiss', { duration: 3000 });
      this.dialogRef.close();
    } catch (err) {
      this.toasts.open(err instanceof Error ? err.message : 'Could not clear the table.', 'Dismiss', { duration: 5000 });
    } finally {
      this.clearing.set(false);
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
